import { commonErrorHandler } from 'models/shared/helpers/errors';
import moment from 'moment';
import React, { useEffect, useState, createContext, useMemo } from 'react';
import { fetchActiveLoanLocations } from 'models/loan/controllers/loan.controller';
import {
	fetchTilesLocations,
	fetchTilesLocationHistory,
} from 'models/geozon/controllers/tile.controller';
import { TileTrackerHistoryLocationDto } from 'models/geozon/interfaces/tile.interface';
import { InterestPointType } from 'models/repossession/enums/repossession.enums';
import { AddressLocation } from './DevicesMap.constants';

interface DevicesMapContextModel {
	loading: boolean;
	locations: AddressLocation[];
	clearContext: () => void;
	selectedMarker?: AddressLocation;
	setSelectedMarker: (point: AddressLocation | undefined) => void;
	locationsHistory: TileTrackerHistoryLocationDto[];
	loadingHistory: boolean;
}

export const devicesMapContext = createContext<DevicesMapContextModel>({
	loading: false,
} as any);

export const DevicesMapContextProvider = ({ children }: { children: any }) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [locations, setLocations] = useState<AddressLocation[]>([]);
	const [selectedMarker, setSelectedMarker] = useState<
		AddressLocation | undefined
	>();
	const [locationsHistory, setLocationsHistory] = useState<
		TileTrackerHistoryLocationDto[]
	>([]);
	const [loadingHistory, setLoadingHistory] = useState<boolean>(false);

	const initializeContext = () => {
		setLoading(true);
		Promise.all([fetchActiveLoanLocations(), fetchTilesLocations()])
			.then(([loans, tiles]) => {
				setLoading(false);
				const addressLocations: AddressLocation[] = loans.map((loan) => ({
					loanId: loan.loanId,
					vehicleId: loan.internalId,
					coordinates: loan.coordinates,
					createdAt: loan.updatedAt,
					id: loan.loanId,
				}));
				const tileLocations: AddressLocation[] = tiles
					.filter((tile) => tile.location)
					.map((tile) => ({
						loanId: tile.vehicle.internalId,
						vehicleId: tile.vehicle.internalId,
						coordinates: {
							lat: tile.location.latitude,
							long: tile.location.longitude,
						},
						createdAt: tile.location.updatedAt,
						id: `${tile._id}_tile`,
						tileId: tile._id,
						locationType:
							Math.abs(
								moment(tile.location.updatedAt).diff(moment(), 'hours'),
							) < 24
								? InterestPointType.signalTileVehicle
								: InterestPointType.noSignalTileVehicle,
					}));
				setLocations([...addressLocations, ...tileLocations]);
			})
			.catch((e) => {
				setLoading(false);
				commonErrorHandler(e);
			});
	};

	const clearContext = () => {
		setLocations([]);
		initializeContext();
	};

	useEffect(() => {
		initializeContext();
	}, []);

	useEffect(() => {
		if (!selectedMarker?.tileId) {
			setLocationsHistory([]);
			return;
		}
		setLoadingHistory(true);
		fetchTilesLocationHistory(selectedMarker.tileId)
			.then((res) => {
				setLoadingHistory(false);
				setLocationsHistory(res.locations);
			})
			.catch((e) => {
				setLoadingHistory(false);
				commonErrorHandler(e);
			});
	}, [selectedMarker]);

	const contextValue: DevicesMapContextModel = useMemo(
		() => ({
			loading,
			locations,
			clearContext,
			selectedMarker,
			setSelectedMarker,
			locationsHistory,
			loadingHistory,
		}),
		[loading, locations, selectedMarker, locationsHistory, loadingHistory],
	);

	return (
		<devicesMapContext.Provider value={contextValue}>
			{children}
		</devicesMapContext.Provider>
	);
};
